import publicConfig from '@/config/public'
import { SupportedLanguages } from '../formatters'

interface RedirectURL {
  url: string
  as: string
}

export interface CookieHook {
  getUserLocaleCookie: () => SupportedLanguages | undefined
  setUserLocaleCookie: (value: SupportedLanguages) => void
  getRedirectURLCookie: () => RedirectURL | null
  setRedirectURLCookie: (value: RedirectURL) => void
  removeRedirectURLCookie: () => void
}

export enum CookieNames {
  USER_LOCALE = 'usr-locale',
  REDIRECT_URL = 'redirect-url',
}

interface CookieOptions {
  path?: string
  maxAge?: number
  expires?: Date
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`))
  return match != null ? decodeURIComponent(match[1]) : undefined
}

const writeCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  if (typeof document === 'undefined') {
    return
  }
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
  if (options.path != null) {
    cookie += `; path=${options.path}`
  }
  if (options.maxAge != null) {
    cookie += `; max-age=${options.maxAge}`
  }
  if (options.expires != null) {
    cookie += `; expires=${options.expires.toUTCString()}`
  }
  if (options.secure) {
    cookie += '; secure'
  }
  if (options.sameSite != null) {
    cookie += `; samesite=${options.sameSite}`
  }
  document.cookie = cookie
}

/* hook for easier browser cookie management */
const useCookies = (): CookieHook => {
  const baseOptions: CookieOptions = {
    secure: publicConfig.environment === 'production',
    sameSite: 'strict',
    path: '/',
  }

  const getUserLocaleCookie: CookieHook['getUserLocaleCookie'] = () => {
    const raw = readCookie(CookieNames.USER_LOCALE)
    const supported = Object.values(SupportedLanguages) as string[]
    return raw != null && supported.includes(raw) ? (raw as SupportedLanguages) : undefined
  }

  const setUserLocaleCookie: CookieHook['setUserLocaleCookie'] = (value) => {
    writeCookie(CookieNames.USER_LOCALE, value, baseOptions)
  }

  // we read/write a redirect URL cookie to send the user back
  // from whence they came after signing in (i.e leaving the app)
  const getRedirectURLCookie: CookieHook['getRedirectURLCookie'] = () => {
    const value = readCookie(CookieNames.REDIRECT_URL)
    if (!value) {
      return null
    }
    // NOTE: the url and as values are passed to router.push
    // IMPORTANT: we use a pipe `|` as a delimiter since it's a disallowed character in URLs
    // CRITICAL: at some point, this used a dash as a delimiter and that broke because they are common characters in URLs
    const [url, as] = value.split('|')
    return { url, as }
  }

  const setRedirectURLCookie: CookieHook['setRedirectURLCookie'] = ({ url, as }: RedirectURL) => {
    // NOTE: the url and as values are passed to router.push
    // IMPORTANT: we use a pipe `|` as a delimiter since it's a disallowed character in URLs
    const value = `${url}|${as}`
    writeCookie(CookieNames.REDIRECT_URL, value, {
      ...baseOptions,
      // we need to specify a lax sameSite policy when setting the cookie in order for it to persist in safari
      sameSite: 'lax',
    })
  }

  const removeRedirectURLCookie: CookieHook['removeRedirectURLCookie'] = () => {
    // Mirror the sameSite policy used when writing the cookie so Chrome
    // honors the deletion during the cross-site post-CAF redirect.
    writeCookie(CookieNames.REDIRECT_URL, '', {
      ...baseOptions,
      sameSite: 'lax',
      maxAge: 0,
      expires: new Date(0),
    })
  }

  return {
    getUserLocaleCookie,
    setUserLocaleCookie,
    getRedirectURLCookie,
    setRedirectURLCookie,
    removeRedirectURLCookie,
  }
}

export default useCookies
export { useCookies }
